import { get, post, request, upload } from './client';
import { isApiError } from './ApiError';
import { showAlert } from '../helpers';

/**
 * Vuex adapter that sits on top of the transport layer.
 *
 * The transport layer knows nothing about the store, so every store side effect
 * belongs here: the loading flag, per-field errors, and alerts. Keeping them
 * apart lets the transport layer be used outside Vuex, and avoids the problem
 * of the old `postApi`, which always showed an alert and therefore pushed
 * modules into writing their own fetcher just to opt out.
 *
 * @param {Object} ctx - Vuex action context.
 * @param {Object} [options] - Adapter options.
 * @param {string|null} [options.loadingMutation='setLoading'] - Mutation that
 *   holds the loading flag. Pass null when the module has none.
 * @param {string|null} [options.errorsMutation='setErrors'] - Mutation that
 *   holds per-field errors. Pass null when the module has none.
 * @param {boolean} [options.alert=true] - Whether failures raise an alert.
 * @returns {{ post: Function, get: Function, upload: Function, request: Function }}
 *   A fetcher bound to this action context.
 *
 * @example
 * async getMdrList(ctx, { date, mcc }) {
 *   const api = createStoreFetcher(ctx);
 *   const res = await api.post(URL, { mcc, version: hash });
 *   return res.data;
 * }
 */
export const createStoreFetcher = (ctx, options = {}) => {
  const {
    loadingMutation = 'setLoading',
    errorsMutation = 'setErrors',
    alert = true,
  } = options;

  /**
   * Commits the loading flag when the module defines that mutation.
   *
   * @param {boolean} value - New loading state.
   * @returns {void}
   */
  const commitLoading = (value) => {
    if (loadingMutation) ctx.commit(loadingMutation, value);
  };

  /**
   * Commits per-field errors when the module defines that mutation.
   *
   * @param {Object} value - Field error map.
   * @returns {void}
   */
  const commitErrors = (value) => {
    if (errorsMutation) ctx.commit(errorsMutation, value);
  };

  /**
   * Runs one request and applies the store side effects around it.
   *
   * A canceled request is normally replaced by a newer one that has already
   * switched the loading flag on, so the flag is left untouched in that case to
   * keep the indicator from flickering.
   *
   * @param {Function} send - Function that performs the request.
   * @param {Object} runOptions - Behaviour flags for this call.
   * @param {boolean} [runOptions.silent] - Suppresses the automatic alert.
   * @param {boolean} [runOptions.loading] - Toggles the loading flag.
   * @param {boolean} [runOptions.resetErrors] - Clears field errors first.
   * @returns {Promise<Object>} The normalized envelope.
   * @throws {ApiError} Rethrown after the side effects have run.
   */
  const run = async (send, runOptions) => {
    const { silent = false, loading = true, resetErrors = true } = runOptions;

    if (loading) commitLoading(true);
    if (resetErrors) commitErrors({});

    try {
      const result = await send();
      if (loading) commitLoading(false);
      return result;
    } catch (error) {
      if (!isApiError(error)) {
        if (loading) commitLoading(false);
        throw error;
      }

      /**
       * Field errors are rendered whenever the response carries them, not only
       * on 422. The contract also allows a 400 to describe per-field problems,
       * and ignoring those would leave the form unmarked while an alert claims
       * the data is wrong.
       */
      if (error.errors) {
        commitErrors(error.errors);
      }

      /**
       * A handled error has already been dealt with globally, for example a 401
       * that triggered a sign-out, so a second alert would be redundant.
       */
      const shouldAlert =
        alert && !silent && !error.handled && !error.isCanceled;

      if (shouldAlert) {
        showAlert(ctx, error.alertMessage, 'mdi-alert', 'red');
      }

      if (loading && !error.isCanceled) commitLoading(false);

      throw error;
    }
  };

  return {
    /**
     * Sends a POST request.
     *
     * @param {string} url - Path relative to the base URL.
     * @param {Object|string} [data] - Request body.
     * @param {Object} [callOptions] - Run options merged with the request config.
     * @returns {Promise<Object>} The normalized envelope.
     */
    post(url, data = null, callOptions = {}) {
      const { silent, loading, resetErrors, ...config } = callOptions;
      return run(() => post(url, data, config), {
        silent,
        loading,
        resetErrors,
      });
    },

    /**
     * Sends a GET request. Field errors are kept by default, since a read
     * rarely invalidates the errors already shown on a form.
     *
     * @param {string} url - Path relative to the base URL.
     * @param {Object} [params] - Query string parameters.
     * @param {Object} [callOptions] - Run options merged with the request config.
     * @returns {Promise<Object>} The normalized envelope.
     */
    get(url, params = null, callOptions = {}) {
      const { silent, loading, resetErrors = false, ...config } = callOptions;
      return run(() => get(url, params, config), {
        silent,
        loading,
        resetErrors,
      });
    },

    /**
     * Uploads a file and reports progress.
     *
     * @param {string} url - Path relative to the base URL.
     * @param {FormData} formData - Payload to upload.
     * @param {Object} [callOptions] - Run options merged with the request config.
     * @param {Function} [callOptions.onProgress] - Progress from 0 to 100.
     * @returns {Promise<Object>} The normalized envelope.
     */
    upload(url, formData, callOptions = {}) {
      const { silent, loading, resetErrors, ...config } = callOptions;
      return run(() => upload(url, formData, config), {
        silent,
        loading,
        resetErrors,
      });
    },

    /**
     * Sends a request built from a full request config.
     *
     * @param {Object} config - Full request config.
     * @param {Object} [callOptions] - Run options.
     * @returns {Promise<Object>} The normalized envelope.
     */
    request(config, callOptions = {}) {
      return run(() => request(config), callOptions);
    },
  };
};

/**
 * Runs a request and falls back to a default value when it fails.
 *
 * The fetcher has already produced the side effects by this point, namely the
 * alert and the field errors, so this helper only shortens actions that just
 * need to return null or false on failure. Anything that is not an
 * {@link ApiError} is rethrown, so genuine bugs still surface.
 *
 * @param {Function} task - Async function that performs the request.
 * @param {*} [fallback=null] - Value returned when an ApiError is caught.
 * @returns {Promise<*>} The task result, or the fallback on failure.
 *
 * @example
 * return attempt(async () => {
 *   const res = await api.post(URL, payload);
 *   return res.data;
 * }, false);
 */
export const attempt = async (task, fallback = null) => {
  try {
    return await task();
  } catch (error) {
    if (!isApiError(error)) throw error;
    return fallback;
  }
};

export default createStoreFetcher;
